import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Grid, Chip, Button,
  Autocomplete, CircularProgress, Alert, Divider, Avatar, List,
  ListItem, ListItemButton, ListItemText, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar,
} from '@mui/material';
import PetsIcon from '@mui/icons-material/Pets';
import TimelineIcon from '@mui/icons-material/Timeline';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ScienceIcon from '@mui/icons-material/Science';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { collection, getDocs, query, where, orderBy, getDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useBranch } from '../context/BranchContext';
import { useAuth } from '../context/AuthContext';
import { generateLabReport, generateLabOrderPDF } from '../services/reportGenerator';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ResponsiveContainer,
} from 'recharts';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const STATUS_CHIP = {
  normal:   { color: '#16A34A', bg: '#DCFCE7' },
  high:     { color: '#DC2626', bg: '#FEE2E2' },
  low:      { color: '#2563EB', bg: '#DBEAFE' },
  critical: { color: '#7F1D1D', bg: '#FEE2E2' },
  unknown:  { color: '#6B7280', bg: '#F3F4F6' },
  reported: { color: '#059669', bg: '#D1FAE5' },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={4} sx={{ p: 1.5, borderRadius: 2, fontSize: '0.8rem' }}>
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>{label}</Typography>
      {payload.map((p) => (
        <Box key={p.name} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color }} />
          <span>{p.name}: <strong>{p.value}</strong></span>
        </Box>
      ))}
    </Paper>
  );
};

export default function PatientLabHistory() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeBranch, filterByBranch } = useBranch();
  const { userData } = useAuth();
  const urlPatientId = searchParams.get('patientId');
  
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedParam, setSelectedParam] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [resampleOpen, setResampleOpen] = useState(false);
  const [resampleJustification, setResampleJustification] = useState('');
  const [resampleSaving, setResampleSaving] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

  const RESAMPLE_REASONS = [
    'Problemas de identificación',
    'Hemólisis',
    'Lipemia',
    'Muestra coagulada',
    'Volumen insuficiente',
    'Contaminación',
    'Muestra deteriorada',
    'Muestra inadecuada',
    'Resultados incompatibles con la situación clínica',
    'Resultados que requieren confirmación',
  ];

  useEffect(() => {
    setLoadingPatients(true);
    const load = async () => {
      try {
        const [pSnap, oSnap] = await Promise.all([
          getDocs(query(collection(db, 'patients'), orderBy('name'))),
          getDocs(collection(db, 'owners')),
        ]);
        const ownersMap = {};
        oSnap.docs.forEach((d) => {
          const o = d.data();
          ownersMap[d.id] = { name: o.name || '', rut: o.rut || '', celular: o.celular || '' };
        });
        const patientList = pSnap.docs.map((d) => {
          const p = { id: d.id, ...d.data() };
          const owner = ownersMap[p.ownerId] || {};
          return { ...p, ownerName: owner.name || '', ownerRut: owner.rut || '', ownerCelular: owner.celular || '' };
        });
        const filtered = filterByBranch(patientList, { includeUnassigned: true });
        setPatients(filtered);
        setLoadingPatients(false);

        if (urlPatientId) {
          const found = filtered.find((p) => p.id === urlPatientId);
          if (found) {
            setSelectedPatient(found);
          } else {
            setLoadingPatient(true);
            const single = await getDoc(doc(db, 'patients', urlPatientId));
            if (single.exists()) {
              const p = { id: single.id, ...single.data() };
              const owner = ownersMap[p.ownerId] || {};
              const patient = { ...p, ownerName: owner.name || '', ownerRut: owner.rut || '' };
              setPatients((prev) => [...prev, patient]);
              setSelectedPatient(patient);
            }
            setLoadingPatient(false);
          }
        }
      } catch (e) {
        console.error(e);
        setLoadingPatients(false);
      }
    };
    load();
  }, [urlPatientId, filterByBranch]);

  useEffect(() => {
    if (!selectedPatient) { setResults([]); return; }
    setLoadingResults(true);
    getDocs(query(
      collection(db, 'labResults'),
      where('patientId', '==', selectedPatient.id)
    )).then((snap) => {
      const results = filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true })
        .filter((r) => r.status === 'reported' || r.status === 'approved')
        .sort((a, b) => {
          const dateA = a.uploadedAt || a.importedAt;
          const dateB = b.uploadedAt || b.importedAt;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB.toDate ? dateB.toDate() - dateA.toDate() : dateB - dateA;
        });
      setResults(results);
      setLoadingResults(false);
    });
  }, [selectedPatient]);

  useEffect(() => {
    if (!selectedPatient) { setOrders([]); return; }
    setLoadingOrders(true);
    getDocs(query(
      collection(db, 'labOrders'),
      where('patientId', '==', selectedPatient.id)
    )).then((snap) => {
      const orders = filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true });
      orders.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      setOrders(orders);
      setLoadingOrders(false);
    });
  }, [selectedPatient]);

  // All unique parameter names across all results
  const allParams = useMemo(() => {
    const names = new Set();
    results.forEach((r) =>
      (r.extractedParameters || []).forEach((p) => names.add(p.paramName))
    );
    return Array.from(names).sort();
  }, [results]);

  // Trend data for selected parameter
  const trendData = useMemo(() => {
    if (!selectedParam) return [];
    return results
      .filter((r) => r.status === 'reported' || r.status === 'approved')
      .map((r) => {
        const param = (r.extractedParameters || []).find((p) => p.paramName === selectedParam);
        if (!param) return null;
        const date = r.uploadedAt?.toDate
          ? r.uploadedAt.toDate().toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })
          : '—';
        return {
          date,
          [selectedParam]: parseFloat(param.value) || null,
          refMin: param.refMin,
          refMax: param.refMax,
        };
      })
      .filter(Boolean)
      .reverse();
  }, [results, selectedParam]);

  const currentParam = useMemo(() => {
    if (!selectedParam || !results.length) return null;
    for (const r of results) {
      const p = (r.extractedParameters || []).find((ep) => ep.paramName === selectedParam);
      if (p) return p;
    }
    return null;
  }, [results, selectedParam]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handlePrintOrder = async (order) => {
    try {
      const [ownerSnap, vetSnap] = await Promise.all([
        selectedPatient?.ownerId ? getDoc(doc(db, 'owners', selectedPatient.ownerId)) : Promise.resolve(null),
        order?.vetId ? getDoc(doc(db, 'professionals', order.vetId)) : Promise.resolve(null),
      ]);
      const owner = ownerSnap?.exists() ? { id: ownerSnap.id, ...ownerSnap.data() } : null;
      const vet = vetSnap?.exists() ? { id: vetSnap.id, ...vetSnap.data() } : null;
      const blob = await generateLabOrderPDF({ order, patient: selectedPatient, owner, vet, branch: activeBranch, userName: userData?.name || userData?.email });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      alert('Error al generar PDF de orden');
    }
  };

  const handlePrintReport = async (result) => {
    try {
      const [orderSnap, ownerSnap, vetSnap] = await Promise.all([
        result?.orderId ? getDoc(doc(db, 'labOrders', result.orderId)) : Promise.resolve(null),
        selectedPatient?.ownerId ? getDoc(doc(db, 'owners', selectedPatient.ownerId)) : Promise.resolve(null),
        result?.reviewedBy ? getDoc(doc(db, 'professionals', result.reviewedBy)) : Promise.resolve(null),
      ]);
      const order = orderSnap?.exists() ? { id: orderSnap.id, ...orderSnap.data() } : null;
      const owner = ownerSnap?.exists() ? { id: ownerSnap.id, ...ownerSnap.data() } : null;
      const vet = vetSnap?.exists() ? { id: vetSnap.id, ...vetSnap.data() } : null;
      const blob = await generateLabReport({ order, result, patient: selectedPatient, owner, vet, branch: activeBranch });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      alert('Error al generar informe PDF');
    }
  };

  const handleCreateResample = async () => {
    if (!selectedResult?.orderId || !resampleJustification) return;
    setResampleSaving(true);
    try {
      const orderSnap = await getDoc(doc(db, 'labOrders', selectedResult.orderId));
      if (!orderSnap.exists()) throw new Error('Orden original no encontrada');
      const original = orderSnap.data();
      const newOrder = {
        ...original,
        parentOrderId: selectedResult.orderId,
        orderNumber: (original.orderNumber || '') + '-C-' + String(Date.now()).slice(-4),
        status: 'pending_upload',
        paymentStatus: 'pagado',
        justification: resampleJustification,
        examResults: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      delete newOrder.id;
      delete newOrder.labResultIds;
      const newRef = await addDoc(collection(db, 'labOrders'), newOrder);
      setOrders((prev) => [{ id: newRef.id, ...newOrder }, ...prev]);
      setResampleOpen(false);
      setResampleJustification('');
      setFeedback({ open: true, message: 'Contramuestra creada y pagada.', severity: 'success' });
    } catch (err) {
      console.error(err);
      setFeedback({ open: true, message: 'Error al crear contramuestra.', severity: 'error' });
    } finally {
      setResampleSaving(false);
    }
  };

  const closeResample = () => {
    setResampleOpen(false);
    setResampleJustification('');
  };

  const closeFeedback = () => setFeedback((prev) => ({ ...prev, open: false }));

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        {urlPatientId && (
          <IconButton onClick={() => navigate(`/patients/${urlPatientId}/record`)} sx={{ color: LAB_PRIMARY }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <TimelineIcon sx={{ color: LAB_PRIMARY, fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Historial de Laboratorio</Typography>
          <Typography variant="caption" color="text.secondary">Seguimiento y tendencias por paciente</Typography>
        </Box>
      </Box>
      {/* Patient selector - solo mostrar si no viene de ficha clínica */}
      {!urlPatientId && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
          <Autocomplete
            options={patients}
            getOptionLabel={(p) =>
              `${p.name} — ${p.species || 'N/D'}${p.breed ? ` · ${p.breed}` : ''}${p.ownerName ? ` (${p.ownerName})` : ''}`
            }
            filterOptions={(options, { inputValue }) => {
              const term = (inputValue || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
              if (!term) return options;
              return options.filter((p) => {
                const haystack = `${p.name || ''} ${p.species || ''} ${p.breed || ''} ${p.ownerName || ''} ${p.ownerRut || ''} ${p.id || ''}`
                  .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                return haystack.includes(term);
              });
            }}
            isOptionEqualToValue={(p, v) => p?.id === v?.id}
            value={selectedPatient}
            onChange={(_, v) => { setSelectedPatient(v); setSelectedResult(null); setSelectedParam(''); }}
            loading={loadingPatients}
            openOnFocus
            autoHighlight
            clearOnBlur
            noOptionsText="No se encontraron pacientes"
            renderInput={(params) => (
              <TextField {...params} label="Buscar paciente por nombre, especie, tutor o RUT" size="small" sx={{ maxWidth: 500 }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <PetsIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />,
                }}
              />
            )}
          />
          {selectedPatient && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: LAB_TEAL, width: 44, height: 44 }}>
                <PetsIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>{selectedPatient.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPatient.species} · {selectedPatient.breed} · {selectedPatient.sex}
                  {selectedPatient.weight ? ` · ${selectedPatient.weight} kg` : ''}
                </Typography>
              </Box>
              <Chip label={`${results.length} exámen(es)`} size="small"
                sx={{ bgcolor: '#EFF6FF', color: LAB_PRIMARY, fontWeight: 700 }} />
            </Box>
          )}
        </Paper>
      )}
      {/* Mostrar info del paciente cuando viene de ficha clínica */}
      {urlPatientId && selectedPatient && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
          {loadingPatient ? (
            <CircularProgress size={24} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: LAB_TEAL, width: 44, height: 44 }}>
                <PetsIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>{selectedPatient.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPatient.species} · {selectedPatient.breed} · {selectedPatient.sex}
                  {selectedPatient.weight ? ` · ${selectedPatient.weight} kg` : ''}
                </Typography>
              </Box>
              <Chip label={`${results.length} exámen(es)`} size="small"
                sx={{ bgcolor: '#EFF6FF', color: LAB_PRIMARY, fontWeight: 700 }} />
            </Box>
          )}
        </Paper>
      )}
      {selectedPatient && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden', mb: 3 }}>
              <Box sx={{ p: 1.5, bgcolor: LAB_PRIMARY }}>
                <Typography variant="subtitle2" fontWeight={700} color="#fff">
                  <ScienceIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Órdenes de laboratorio
                </Typography>
              </Box>
              {loadingOrders ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
              ) : orders.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    Sin órdenes de laboratorio.
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ p: 0, maxHeight: '30vh', overflowY: 'auto' }}>
                  {orders.map((o, i) => (
                    <React.Fragment key={o.id}>
                      {i > 0 && <Divider />}
                      <ListItemButton
                        onClick={() => navigate(`/lab/orders/${o.id}`)}
                        sx={{ '&.Mui-selected': { bgcolor: '#EFF6FF' } }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={700}>
                                {o.orderNumber || '—'}
                              </Typography>
                              <Chip label={o.status || 'solicitado'} size="small"
                                sx={{ fontSize: '0.6rem', height: 16 }} />
                            </Box>
                          }
                          primaryTypographyProps={{ component: 'div' }}
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary">{formatDate(o.createdAt)}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {o.examsRequested?.map((e) => e.examTypeName).join(', ') || '—'}
                              </Typography>
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        <Button size="small" startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                          onClick={(e) => { e.stopPropagation(); handlePrintOrder(o); }}
                          sx={{ minWidth: 0, p: 0.5, color: LAB_TEAL }}>
                          Imprimir
                        </Button>
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          {/* LEFT: exam timeline */}
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, bgcolor: LAB_PRIMARY }}>
                <Typography variant="subtitle2" fontWeight={700} color="#fff">
                  <ScienceIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Historial de exámenes
                </Typography>
              </Box>
              {loadingResults ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
              ) : results.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    Sin exámenes registrados.
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ p: 0, maxHeight: '60vh', overflowY: 'auto' }}>
                  {results.map((r, i) => {
                    const stCfg = STATUS_CHIP[r.status] || STATUS_CHIP.unknown;
                    const critCount = (r.extractedParameters || []).filter((p) => p.status === 'critical').length;
                    return (
                      <ListItem
                        key={r.id}
                        disablePadding
                        divider={i > 0}
                        secondaryAction={
                          <Button size="small" startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                            onClick={(e) => { e.stopPropagation(); handlePrintReport(r); }}
                            sx={{ minWidth: 0, p: 0.5, color: LAB_TEAL }}>
                            Imprimir
                          </Button>
                        }
                      >
                        <ListItemButton
                          selected={selectedResult?.id === r.id}
                          onClick={() => setSelectedResult(r)}
                          sx={{ '&.Mui-selected': { bgcolor: '#EFF6FF' }, pr: 8 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 120 }}>
                                  {r.examTypeName || 'Examen'}
                                </Typography>
                                {critCount > 0 && (
                                  <Chip label={`${critCount} crítico`} size="small"
                                    sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontSize: '0.6rem', height: 16, fontWeight: 700 }} />
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ component: 'div' }}
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">{formatDate(r.uploadedAt)}</Typography>
                                <Chip label={r.status === 'reported' ? 'Informado' : r.status} size="small"
                                  sx={{ ml: 0.5, bgcolor: stCfg.bg, color: stCfg.color, fontSize: '0.6rem', height: 16 }} />
                              </Box>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Paper>
          </Grid>

          {/* RIGHT: result detail + trend chart */}
          <Grid
            size={{
              xs: 12,
              md: 8
            }}>
            {selectedResult ? (
              <Box>
                <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>
                      {selectedResult.examTypeName} — {formatDate(selectedResult.uploadedAt)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {selectedResult.orderId && (
                        <Button size="small" variant="outlined" onClick={() => setResampleOpen(true)}
                          startIcon={<ScienceIcon />} sx={{ borderColor: '#D97706', color: '#D97706', fontSize: '0.75rem' }}>
                          Solicitar contramuestra
                        </Button>
                      )}
                      {selectedResult.reportUrl && (
                        <Button size="small" variant="outlined" href={selectedResult.reportUrl} target="_blank"
                          startIcon={<DownloadIcon />} sx={{ borderColor: LAB_TEAL, color: LAB_TEAL, fontSize: '0.75rem' }}>
                          Informe PDF
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {(selectedResult.extractedParameters || []).map((p, i) => {
                      const st = STATUS_CHIP[p.status] || STATUS_CHIP.unknown;
                      return (
                        <Chip
                          key={i}
                          label={`${p.paramName}: ${p.value || '—'} ${p.unit || ''}`}
                          size="small"
                          onClick={() => setSelectedParam(p.paramName)}
                          sx={{
                            bgcolor: selectedParam === p.paramName ? LAB_PRIMARY : st.bg,
                            color: selectedParam === p.paramName ? '#fff' : st.color,
                            fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
                            border: `1.5px solid ${selectedParam === p.paramName ? LAB_PRIMARY : 'transparent'}`,
                          }}
                        />
                      );
                    })}
                  </Box>
                </Paper>

                {/* Trend chart */}
                {allParams.length > 0 && (
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>
                        Tendencia del parámetro
                      </Typography>
                      <TextField select size="small" label="Parámetro" value={selectedParam}
                        onChange={(e) => setSelectedParam(e.target.value)} sx={{ minWidth: 200 }}>
                        <MenuItem value="">Seleccionar…</MenuItem>
                        {allParams.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                      </TextField>
                    </Box>

                    {selectedParam && trendData.length >= 2 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={trendData} margin={{ top: 8, right: 16, bottom: 0, left: -10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {currentParam?.refMin !== null && currentParam?.refMin !== undefined && (
                            <ReferenceLine y={currentParam.refMin} stroke="#16A34A" strokeDasharray="4 2"
                              label={{ value: 'Mín.', position: 'right', fontSize: 10, fill: '#16A34A' }} />
                          )}
                          {currentParam?.refMax !== null && currentParam?.refMax !== undefined && (
                            <ReferenceLine y={currentParam.refMax} stroke="#DC2626" strokeDasharray="4 2"
                              label={{ value: 'Máx.', position: 'right', fontSize: 10, fill: '#DC2626' }} />
                          )}
                          <Line
                            type="monotone" dataKey={selectedParam}
                            stroke={LAB_PRIMARY} strokeWidth={2.5}
                            dot={{ fill: LAB_PRIMARY, r: 4 }}
                            activeDot={{ r: 6, fill: LAB_TEAL }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : selectedParam ? (
                      <Alert severity="info">Se necesitan al menos 2 resultados informados para mostrar tendencia.</Alert>
                    ) : (
                      <Typography color="text.secondary" variant="body2" sx={{ textAlign: 'center', py: 3 }}>
                        Selecciona un parámetro o haz clic en un chip de valor para ver su tendencia histórica.
                      </Typography>
                    )}
                  </Paper>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: 360, color: '#CBD5E1' }}>
                <TimelineIcon sx={{ fontSize: 64 }} />
                <Typography color="text.disabled" mt={1}>
                  Selecciona un examen para ver el detalle y tendencias
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      {!selectedPatient && !urlPatientId && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#CBD5E1' }}>
          <PetsIcon sx={{ fontSize: 72 }} />
          <Typography color="text.disabled" mt={1}>Busca un paciente para ver su historial de laboratorio</Typography>
        </Box>
      )}

      <Dialog open={resampleOpen} onClose={closeResample} maxWidth="sm" fullWidth>
        <DialogTitle>Solicitar contramuestra</DialogTitle>
        <DialogContent dividers>
          <TextField select fullWidth size="small" label="Justificación" value={resampleJustification}
            onChange={(e) => setResampleJustification(e.target.value)}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value=""><em>Seleccionar…</em></MenuItem>
            {RESAMPLE_REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResample} disabled={resampleSaving}>Cancelar</Button>
          <Button onClick={handleCreateResample} disabled={!resampleJustification || resampleSaving}
            variant="contained" size="small">
            {resampleSaving ? <CircularProgress size={16} /> : 'Solicitar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={feedback.open} autoHideDuration={4000} onClose={closeFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={closeFeedback} severity={feedback.severity} sx={{ width: '100%' }}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
